import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Send, Calendar, Clock, FileCheck, ArrowLeft, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientPrescriptionsPage = () => {
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  const fetchPrescriptions = async () => {
    try {
      const response = await axios.get(`${API}/prescriptions/my-prescriptions`);
      setPrescriptions(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (prescription) => {
    try {
      const response = await axios.get(`${API}/prescriptions/${prescription.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const d = new Date(prescription.generated_at);
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      const mesAnio = `${meses[d.getMonth()]} ${d.getFullYear()}`;
      link.setAttribute('download', `${prescription.client_name} - Receta Medica - ${mesAnio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al descargar');
    }
  };

  const handleResendEmail = async (prescription) => {
    try {
      toast.loading('Enviando a tu correo...', { id: 'resend' });
      await axios.post(`${API}/prescriptions/${prescription.id}/send-email`);
      toast.success('Receta enviada a tu correo', { id: 'resend' });
      fetchPrescriptions();
    } catch (error) {
      toast.error('Error al enviar', { id: 'resend' });
    }
  };

  const handleViewOnline = async (prescription) => {
    try {
      const response = await axios.get(`${API}/prescriptions/${prescription.id}/pdf?view=inline`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('Error al abrir la receta');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in" data-testid="client-prescriptions-page">
      {/* Back + Header */}
      <div className="pt-1">
        <button
          onClick={() => navigate('/client/health-tests')}
          className="flex items-center gap-1.5 text-sm text-blue-500 font-semibold mb-3 hover:gap-2.5 transition-all duration-200"
          data-testid="back-to-health"
        >
          <ArrowLeft size={16} /> Volver a Test de Salud
        </button>
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Mis Recetas
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Tus recetas médicas asignadas</p>
      </div>

      {/* Prescriptions List */}
      {prescriptions.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText size={32} className="text-blue-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin recetas médicas</p>
          <p className="text-sm text-gray-400 mt-1">Aún no tienes recetas asignadas</p>
        </div>
      ) : (
        <div className="space-y-3">
          {prescriptions.map((prescription, idx) => (
            <div 
              key={prescription.id} 
              className="bg-white rounded-3xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              style={{ animationDelay: `${idx * 80}ms` }}
              data-testid={`prescription-card-${prescription.id}`}
            >
              {/* Color strip */}
              <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />

              <div className="p-5 flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileCheck size={22} className="text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm leading-tight">
                    {prescription.template_name}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">{prescription.prescription_date}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">
                        {new Date(prescription.generated_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  {prescription.emailed && (
                    <span className="inline-flex items-center gap-1 mt-2.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full uppercase tracking-wider">
                      Enviado por email
                    </span>
                  )}
                </div>
              </div>

              {/* Actions - Dashed separator for ticket effect */}
              <div className="mx-5 border-t border-dashed border-gray-200" />
              <div className="flex px-2 py-2">
                <button
                  onClick={() => handleViewOnline(prescription)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 mx-1 text-sm font-semibold text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all duration-200"
                  data-testid={`view-online-${prescription.id}`}
                >
                  <Eye size={16} />
                  Ver Online
                </button>
                <button
                  onClick={() => handleDownload(prescription)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 mx-1 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200"
                  data-testid={`download-${prescription.id}`}
                >
                  <Download size={16} />
                  Descargar
                </button>
                <button
                  onClick={() => handleResendEmail(prescription)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 mx-1 text-sm font-semibold text-violet-600 hover:bg-violet-50 rounded-xl transition-all duration-200"
                  data-testid={`resend-${prescription.id}`}
                >
                  <Send size={16} />
                  Enviar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientPrescriptionsPage;
